import { KIND } from './kinds.js';
import { signEvent } from './event.js';
import { normalizePubkey } from './keys.js';
import { encryptTo, decryptFrom } from './nip44.js';
import { attachmentTag, parseAttachmentTags, checkAttachment, BLOB_LIMITS } from './blob.js';
import { tagValue } from './util.js';

/**
 * Content types a conforming sender may declare (HP-08).
 *
 * The tag was signed but never inspected by anyone, so the only thing keeping a
 * client safe was its "unknown means text/plain" fallback. Validating at the
 * point of signing means the declaration cannot be a surprise later.
 */
export const CONTENT_TYPES = ['text/plain', 'text/markdown'];

function checkContentType(contentType) {
  if (!CONTENT_TYPES.includes(contentType)) {
    throw new Error(`content type must be one of ${CONTENT_TYPES.join(', ')}`);
  }
  return contentType;
}

/**
 * The two message shapes, and why they are different.
 *
 * BROADCAST is fanout-on-read: one signed event serves every subscriber, who
 * pulls it by author+channel filter. Sending to a million people costs one
 * signature and one row. Nobody learns who read it.
 *
 * DIRECTED is fanout-on-write and encrypted per recipient: one event per
 * person, ciphertext only at rest. Costs scale with the audience, which is
 * correct -- personal messages should not be cheap to blast.
 */

export function buildBroadcast(
  { channel, subject, body, contentType = 'text/plain', expiresAt, createdAt, attachments = [] },
  senderSecretKey,
) {
  const tags = [['c', channel], ['subject', subject], ['content-type', checkContentType(contentType)]];
  if (expiresAt) tags.push(['expiration', String(expiresAt)]);
  // Broadcast attachments are public, so their descriptors go in tags and the
  // blob is stored in the clear. The hash is covered by the signature.
  if (attachments.length > BLOB_LIMITS.maxPerEvent) {
    throw new Error(`at most ${BLOB_LIMITS.maxPerEvent} attachments per message`);
  }
  for (const attachment of attachments) {
    const error = checkAttachment(attachment);
    if (error) throw new Error(error);
    tags.push(attachmentTag(attachment));
  }
  return signEvent({ kind: KIND.BROADCAST, content: body, tags, created_at: createdAt }, senderSecretKey);
}

export function buildDirected(
  { channel, recipient, subject, body, contentType = 'text/plain', expiresAt, createdAt, attachments = [] },
  senderSecretKey,
) {
  const to = normalizePubkey(recipient);
  if (attachments.length > BLOB_LIMITS.maxPerEvent) {
    throw new Error(`at most ${BLOB_LIMITS.maxPerEvent} attachments per message`);
  }
  for (const attachment of attachments) {
    const error = checkAttachment(attachment);
    if (error) throw new Error(error);
    if (!attachment.key) throw new Error('a directed attachment must carry its decryption key');
  }
  // Subject and attachment keys are inside the ciphertext: the relay must learn
  // neither what this is about nor how to open the blobs it stores.
  const payload = JSON.stringify({
    subject,
    body,
    content_type: checkContentType(contentType),
    attachments: attachments.map((a) => ({
      hash: a.hash, filename: a.filename, mime: a.mime, size: a.size, key: a.key,
    })),
  });
  const tags = [['p', to], ['c', channel]];
  if (expiresAt) tags.push(['expiration', String(expiresAt)]);
  return signEvent(
    { kind: KIND.DIRECTED, content: encryptTo(payload, senderSecretKey, to), tags, created_at: createdAt },
    senderSecretKey,
  );
}

/** Decrypt a DIRECTED event addressed to us. */
export function openDirected(event, recipientSecretKey) {
  const plaintext = decryptFrom(event.content, recipientSecretKey, event.pubkey);
  const parsed = JSON.parse(plaintext);
  // Descriptors come from inside the ciphertext, so they are as trustworthy as
  // the message -- but still validated, because a sender may be buggy.
  const attachments = (Array.isArray(parsed.attachments) ? parsed.attachments : [])
    .filter((a) => checkAttachment(a) === null && typeof a.key === 'string');
  return {
    channel: tagValue(event, 'c'),
    subject: parsed.subject ?? '',
    body: parsed.body ?? '',
    contentType: CONTENT_TYPES.includes(parsed.content_type) ? parsed.content_type : 'text/plain',
    attachments,
  };
}

export function readBroadcast(event) {
  // An unrecognised declaration falls back to plain text rather than being
  // honoured: rendering is the dangerous step, so the safe reading wins.
  const declared = tagValue(event, 'content-type');
  return {
    channel: tagValue(event, 'c'),
    subject: tagValue(event, 'subject') ?? '',
    body: event.content,
    contentType: CONTENT_TYPES.includes(declared) ? declared : 'text/plain',
    attachments: parseAttachmentTags(event),
  };
}

/** Scope string a sender key needs in order to publish this event. */
export function requiredScopeFor(event) {
  const channel = tagValue(event, 'c') ?? '*';
  switch (event.kind) {
    case KIND.BROADCAST: return `broadcast:${channel}`;
    case KIND.DIRECTED: return `transactional:${channel}`;
    case KIND.CONSENT_RECEIPT: return `consent:${channel}`;
    case KIND.CHANNEL: return `channel:${channel}`;
    default: return `*`;
  }
}

export function isExpired(event, at = Math.floor(Date.now() / 1000)) {
  const exp = tagValue(event, 'expiration');
  return exp !== undefined && Number(exp) < at;
}
